/**
 * Dashboard Page
 *
 * Main dashboard overview with system health, recent activity,
 * and quick actions. Auto-refreshes every 30 seconds.
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Activity,
  Database,
  FileText,
  TrendingUp,
  RefreshCw,
  Play,
  AlertCircle,
  CheckCircle,
  Loader2,
  Zap,
  Video
} from 'lucide-react';
import { useSystemStatus } from '@/hooks/api/useSystem';
import { useContent } from '@/hooks/api/useContent';
import { useManualIngest } from '@/hooks/api/useArticles';
import { useTaskQueue } from '@/contexts/TaskQueueContext';
import { TaskQueueModal } from '@/components/TaskQueueModal';
import { useAuth } from '@/hooks/auth/useAuth';
import { formatDistanceToNow } from 'date-fns';

export function DashboardPage() {
  const { isSuperAdmin } = useAuth();

  // Real API data instead of mock data
  const {
    data: systemStatus,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus
  } = useSystemStatus();

  // Phase 8: Use shared SSE context for real-time task queue updates
  // Single connection shared across all components (dashboard + modal)
  const { data: taskQueueData } = useTaskQueue();

  const {
    data: recentContent,
    isLoading: contentLoading,
  } = useContent(
    {
      limit: 5,
      page: 1,
      content_type: 'article',
      status: 'completed',
      order_by: 'created_at',
      order: 'desc',
    },
    { enabled: true },
  );

  const manualIngest = useManualIngest();

  // Task Queue Modal state
  const [taskQueueModalOpen, setTaskQueueModalOpen] = useState(false);

  const handleRefresh = () => {
    refetchStatus();
  };

  const handleManualIngestion = () => {
    manualIngest.mutate({});
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            System overview and quick actions
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={statusLoading}
        >
          {statusLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {/* Error Alert */}
      {statusError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load system status. Please check your connection and try again.
          </AlertDescription>
        </Alert>
      )}

      {/* System health cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* API Service Status */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">API Service</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statusLoading ? (
              <div className="space-y-2">
                <div className="h-8 w-20 bg-muted animate-pulse rounded" />
                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {systemStatus?.api?.status === 'healthy' ? 'Online' : 'Offline'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  <Badge
                    variant={systemStatus?.api?.status === 'healthy' ? 'secondary' : 'destructive'}
                    className="text-xs"
                  >
                    {systemStatus?.api?.status || 'Unknown'}
                  </Badge>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Database Status */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Database</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statusLoading ? (
              <div className="space-y-2">
                <div className="h-8 w-24 bg-muted animate-pulse rounded" />
                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {systemStatus?.database?.status === 'healthy' ? 'Connected' : 'Disconnected'}
                </div>
                {isSuperAdmin && (
                  <div className="text-xs text-muted-foreground mt-1">
                    <Badge variant="secondary" className="text-xs">
                      {systemStatus?.database?.total_articles || 0} articles
                    </Badge>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Task Queue Status */}
        <Card
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setTaskQueueModalOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Task Queue</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statusLoading ? (
              <div className="space-y-2">
                <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
              </div>
            ) : (
              <>
                {(() => {
                  const activeCount = taskQueueData?.metrics?.active_count ?? 0;
                  // Phase 10: Use DB queue_state count (same as TaskQueueModal) for consistency
                  const queuedCount = taskQueueData?.metrics?.queued_count ?? taskQueueData?.queued_content?.length ?? 0;
                  const isWorkerHealthy = systemStatus?.celery?.status === 'healthy';
                  const isQueueActive = activeCount > 0 || queuedCount > 0;

                  // Phase 3.5: Calculate content type breakdown
                  const activeTasks = taskQueueData?.active_tasks ?? [];
                  const articleCount = activeTasks.filter((task: { content_type?: string }) => task.content_type === 'article').length;
                  const videoCount = activeTasks.filter((task: { content_type?: string }) => task.content_type === 'video').length;

                  return (
                    <>
                      <div className="text-2xl font-bold">
                        Worker: {isWorkerHealthy ? 'Connected' : 'Down'}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Queue: {isQueueActive ? 'Active' : 'Empty'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-2 space-y-2">
                        {/* Phase 3.5: Unified content count */}
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {activeCount} active
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {queuedCount} queued
                          </Badge>
                        </div>

                        {/* Phase 3.5: Content type breakdown */}
                        {activeCount > 0 && (
                          <div className="flex items-center gap-2">
                            {articleCount > 0 && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                <FileText className="h-3 w-3 mr-1 inline" />
                                {articleCount} {articleCount === 1 ? 'article' : 'articles'}
                              </Badge>
                            )}
                            {videoCount > 0 && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                <Video className="h-3 w-3 mr-1 inline" />
                                {videoCount} {videoCount === 1 ? 'video' : 'videos'}
                              </Badge>
                            )}
                          </div>
                        )}

                        <div className="text-xs text-muted-foreground">
                          Click to view details
                        </div>
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </CardContent>
        </Card>

        {/* Total Content */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Content</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statusLoading ? (
              <div className="space-y-2">
                <div className="h-8 w-12 bg-muted animate-pulse rounded" />
                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {systemStatus?.database?.total_content ?? systemStatus?.database?.total_articles ?? 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  <TrendingUp className="h-3 w-3 inline mr-1" />
                  {`${recentContent?.items?.length || 0} recent`}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>


      {/* Quick actions and recent activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Quick actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Common administrative tasks
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              className="w-full justify-start bg-foreground text-background hover:bg-foreground/90"
              onClick={handleManualIngestion}
              disabled={manualIngest.isPending}
            >
              {manualIngest.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {manualIngest.isPending ? 'Triggering...' : 'Trigger Manual Ingestion'}
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => { window.location.href = '/content?tab=articles'; }}
            >
              <FileText className="h-4 w-4 mr-2" />
              View Latest Content
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => { window.location.href = '/system'; }}
            >
              <Activity className="h-4 w-4 mr-2" />
              System Status Details
            </Button>
          </CardContent>
        </Card>

        {/* Recent activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Articles</CardTitle>
            <CardDescription>
              Latest ingested articles
            </CardDescription>
          </CardHeader>
          <CardContent>
            {contentLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="h-2 w-2 rounded-full bg-muted animate-pulse" />
                    <div className="flex-1 space-y-1">
                      <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
                      <div className="h-3 w-1/2 bg-muted animate-pulse rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentContent?.items?.length ? (
              <div className="space-y-4">
                {recentContent.items.slice(0, 3).map((item) => (
                  <div key={item.content_id} className="flex items-center space-x-3">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium line-clamp-1">{item.title}</p>
                      <p className="text-xs text-muted-foreground">
                        Publisher ID: {item.publisher_id || 'Unknown'} • {' '}
                        {item.published_at ? formatDistanceToNow(new Date(item.published_at), { addSuffix: true }) : 'Unknown'}
                      </p>
                    </div>
                  </div>
                ))}
                {recentContent.items.length > 3 && (
                  <div className="pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => { window.location.href = '/content?tab=articles'; }}
                    >
                      View all {recentContent.pagination?.total || recentContent.items.length} articles
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">No articles found</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={handleManualIngestion}
                  disabled={manualIngest.isPending}
                >
                  {manualIngest.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  Trigger Ingestion
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manual Ingestion Status */}
      {manualIngest.isSuccess && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Manual ingestion completed successfully! New articles should appear shortly.
          </AlertDescription>
        </Alert>
      )}

      {manualIngest.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to trigger manual ingestion. Please try again or check system status.
          </AlertDescription>
        </Alert>
      )}

      {/* Task Queue Modal */}
      <TaskQueueModal
        open={taskQueueModalOpen}
        onOpenChange={setTaskQueueModalOpen}
      />
    </div>
  );
}
